import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import MD5 from 'crypto-js/md5';
import { buildTree, ITreeNode } from '../../components/General/DynamicTree/DynamicTreeUtils';
import {
    urlParamReplicaId,
    urlProjectCustomViewSave,
    urlProjectFields,
    urlProjectFieldsSchema,
    urlProjectFieldsSchemaCustom,
    urlProjectFieldsCustom,
    urlProjectViewStructure,
} from '../../Constants';
import { IAction, IAppReduxStore, SlicePropsWithData } from '../store';
import { IFieldConfig } from './documents/sliceProjectDocuments';
import { isEqual } from 'lodash';
import { apiFetch } from '../../util/ProjectUtils';

let abortController: AbortController | null = null;

const urlsMapper = {
    fields: {
        fields: urlProjectFields,
        fieldsCustom: urlProjectFieldsCustom,
    },
    schema: {
        schema: urlProjectFieldsSchema,
        schemaCustom: urlProjectFieldsSchemaCustom,
    },
};

/**
 * Initial slice state value.
 */
const initialState: IProjectViewDataState = {
    structure: {
        data: null,
        viewTree: null,
        loading: false,
        error: null,
    },
    fields: {
        data: [],
        loading: false,
        error: null,
    },
    fieldsCustom: {
        data: [],
        loading: false,
        error: null,
    },
    schema: {
        data: [],
        loading: false,
        error: null,
    },
    schemaCustom: {
        data: [],
        loading: false,
        error: null,
    },
    columnsViewMode: 'defaultView',
    dataToSave: null,
    loadingSaveCustomColumnsView: false,
    errorSaveCustomColumnsView: null,
    loadingDeleteCustomColumnsView: false,
    errorDeleteCustomColumnsView: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    setColumnsViewMode(state: IProjectViewDataState, action: IAction<ColumnsViewModeType>) {
        state.columnsViewMode = action.payload;
    },

    setDataToSave(state: IProjectViewDataState, action: IAction<IDataToSave>) {
        state.dataToSave = action.payload;
    },
    resetCustomColumnsViewError(state: IProjectViewDataState) {
        state.fieldsCustom.error = null;
        state.schemaCustom.error = null;
        state.errorDeleteCustomColumnsView = null;
        state.errorSaveCustomColumnsView = null;
    },
};

/**
 * Creates a MD5 Hash for a node's category'.
 * @param node - Target node.
 * @returns MD5 hash.
 */
const getMD5 = (node: IViewStructureNode) => MD5(node.category).toString();

/**
 * Adds missing category nodes to an existing array.
 * @param categories - Category node array.
 * @returns Updated array.
 */
const addMissingCategories = (categories: IViewStructureNode[]): IViewStructureNode[] => {
    /** Array that will hold the missin category nodes. */
    const missing: IViewStructureNode[] = [];

    /** Loop over existing categories. */
    categories.forEach(x => {
        /** Create ID. */
        x.id = getMD5(x);

        /** Split category value into individual parts */
        x.category.split('\\').forEach((_, i, arr) => {
            /** Reconstruct category path. */
            const categoryPath = arr.slice(0, i + 1).join('\\');

            /** If the category isn't already in the input array, then add it. */
            if (![...categories, ...missing].filter(z => z.category === categoryPath).length) {
                missing.push({
                    category: categoryPath,
                    docCount: '0',
                    id: MD5(categoryPath).toString(),
                });
            }
        });
    });

    return [].concat(categories, missing);
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches the document sorting structure for a given view in a project database.
     * @param config - Object that contains the project database's replicaId, as well as the view to load the sorting structure for.
     * @returns Promise for the HTTP request.
     */
    getProjectViewStructure: async (config: {
        replicaId: string;
        viewName: string;
        columnName: string;
    }): Promise<IViewStructureNode[]> => {
        try {
            abortController?.abort();
            abortController = new AbortController();

            const { replicaId, viewName, columnName } = config;
            const url = urlProjectViewStructure.replace(urlParamReplicaId, replicaId);

            const request: RequestInit = {
                headers: {
                    viewName,
                    columnName,
                },
                signal: abortController.signal,
            };
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Async function that fetches the available field configurations for a given project database.
     * @returns Promise for the HTTP request.
     */
    getProjectFieldsOrFieldsSchema: async ({
        replicaId,
        viewName,
        requestKey,
        urlsConfigKey,
    }: IFieldsAndFieldsSchemaConfig & {
        urlsConfigKey: string;
    }): Promise<IFieldConfig[] | string[]> => {
        try {
            const url = urlsMapper[requestKey][urlsConfigKey].replace(urlParamReplicaId, replicaId);
            const request: RequestInit = {
                headers: {
                    viewName,
                },
                signal: abortController.signal,
            };
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    getProjectDefaultAndCustomFieldsOrFieldsSchema: async (
        config: IFieldsAndFieldsSchemaConfig,
        thunkApi,
    ): Promise<ProjectFieldsResponse> => {
        try {
            const { requestKey } = config;
            const result = await Promise.all(
                Object.keys(urlsMapper[requestKey]).map(urlsConfigKey =>
                    thunkApi.dispatch(getProjectFieldsOrFieldsSchema({ ...config, urlsConfigKey })),
                ),
            );

            const response = result.reduce(
                (obj, res) => {
                    if (res?.type?.includes('fulfilled')) {
                        obj.success = {
                            ...obj.success,
                            [res?.meta?.arg?.urlsConfigKey]: res.payload,
                        };
                    } else if (!res.meta['aborted']) {
                        obj.error = {
                            ...obj.error,
                            [res?.meta?.arg?.urlsConfigKey]: res.error,
                        };
                    }

                    return obj;
                },
                { success: {}, error: {} },
            );
            return Promise.resolve(response);
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    saveCustomColumnsView: async (
        {
            replicaId,
            viewName,
        }: {
            replicaId: string;
            viewName: string;
        },
        thunkApi,
    ): Promise<void> => {
        try {
            const { fields, fieldSchema } = thunkApi.getState().projectViewData.dataToSave;

            /** Note: Backend only accepts data in form-format! */
            const formData = new FormData();
            formData.append('viewName', viewName);
            formData.append('fields', fields);
            formData.append('fieldSchema', fieldSchema);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const url = urlProjectCustomViewSave.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve()
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    deleteCustomColumnsView: async ({
        replicaId,
        viewName,
    }: {
        replicaId: string;
        viewName: string;
    }): Promise<void> => {
        try {
            /** Note: Backend only accepts data in form-format! */
            const formData = new FormData();
            formData.append('viewName', viewName);

            const request: RequestInit = {
                method: 'DELETE',
                body: formData,
            };

            const url = urlProjectCustomViewSave.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve()
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectViewDataState>) => {
        /**
         * Reducers for the 'getProjectViewStructure' thunk.
         */

        builder.addCase(getProjectViewStructure.pending, (state: Draft<IProjectViewDataState>) => {
            state.structure.data = null;
            state.structure.viewTree = null;
            state.structure.loading = true;
            state.structure.error = null;
        });
        builder.addCase(
            getProjectViewStructure.fulfilled,
            (state: Draft<IProjectViewDataState>, action: IAction<IViewStructureNode[]>) => {
                abortController = null;
                state.structure.loading = false;
                state.structure.error = null;

                const structure = addMissingCategories(action.payload);

                state.structure.viewTree = buildTree<IViewStructureNode>(
                    structure,
                    'category',
                    '\\',
                );
                state.structure.data = structure;
            },
        );
        builder.addCase(
            getProjectViewStructure.rejected,
            (state: Draft<IProjectViewDataState>, action: IAction<SerializedError>) => {
                abortController = null;
                state.structure.loading = false;
                if (!action.meta['aborted']) {
                    state.structure.error = action.error;
                }
                state.structure.data = null;
                state.structure.viewTree = null;
            },
        );

        /**
         * Reducers for the 'getProjectDefaultAndCustomFieldsOrFieldsSchema' thunk.
         */

        builder.addCase(
            getProjectDefaultAndCustomFieldsOrFieldsSchema.pending,
            (
                state: Draft<IProjectViewDataState>,
                action: IAction<undefined, IFieldsAndFieldsSchemaConfig>,
            ) => {
                const { requestKey } = action?.meta?.arg || { requestKey: 'fields' };

                state[requestKey].data = [];
                state[requestKey].loading = true;
                state[requestKey].error = null;

                state[`${requestKey}Custom`].data = [];
                state[`${requestKey}Custom`].loading = true;
                state[`${requestKey}Custom`].error = null;
            },
        );
        builder.addCase(
            getProjectDefaultAndCustomFieldsOrFieldsSchema.fulfilled,
            (state: Draft<IProjectViewDataState>, action: IAction<ProjectFieldsResponse>) => {
                Object.keys(action.payload.success).map(ac => {
                    state[ac].loading = false;
                    state[ac].error = null;
                    state[ac].data = action.payload.success[ac];
                });
                Object.keys(action.payload.error).map(ac => {
                    state[ac].loading = false;
                    state[ac].error = action.payload.error[ac];
                    state[ac].data = [];
                });
            },
        );
        builder.addCase(
            getProjectDefaultAndCustomFieldsOrFieldsSchema.rejected,
            (
                state: Draft<IProjectViewDataState>,
                action: IAction<SerializedError, IFieldsAndFieldsSchemaConfig>,
            ) => {
                const { requestKey } = action?.meta?.arg || { requestKey: 'fields' };

                state[requestKey].loading = false;
                state[requestKey].data = [];

                state[`${requestKey}Custom`].loading = false;
                state[`${requestKey}Custom`].data = [];

                if (!action.meta['aborted']) {
                    state[requestKey].error = action.error;
                    state[`${requestKey}Custom`].error = action.error;
                }
            },
        );

        /**
         * Reducers for the 'saveCustomColumnsView' thunk.
         */

        builder.addCase(saveCustomColumnsView.pending, (state: Draft<IProjectViewDataState>) => {
            state.loadingSaveCustomColumnsView = true;
            state.errorSaveCustomColumnsView = null;
        });
        builder.addCase(saveCustomColumnsView.fulfilled, (state: Draft<IProjectViewDataState>) => {
            state.loadingSaveCustomColumnsView = false;
            state.dataToSave = null;
            state.errorSaveCustomColumnsView = null;
        });
        builder.addCase(
            saveCustomColumnsView.rejected,
            (state: Draft<IProjectViewDataState>, action: IAction<SerializedError>) => {
                state.loadingSaveCustomColumnsView = false;
                state.errorSaveCustomColumnsView = action.error;
            },
        );

        /**
         * Reducers for the 'deleteCustomColumnsView' thunk.
         */
        builder.addCase(deleteCustomColumnsView.pending, (state: IProjectViewDataState) => {
            state.loadingDeleteCustomColumnsView = false;
            state.errorDeleteCustomColumnsView = null;
        });
        builder.addCase(deleteCustomColumnsView.fulfilled, (state: IProjectViewDataState) => {
            state.loadingDeleteCustomColumnsView = false;
            state.errorDeleteCustomColumnsView = null;
            state.dataToSave = null;
        });
        builder.addCase(
            deleteCustomColumnsView.rejected,
            (state: IProjectViewDataState, action: IAction<SerializedError>) => {
                state.errorDeleteCustomColumnsView = action.error;
                state.loadingDeleteCustomColumnsView = false;
            },
        );
    },
};

export const getProjectViewStructure = createAsyncThunk(
    'project/view',
    thunks.getProjectViewStructure,
);
export const getProjectFieldsOrFieldsSchema = createAsyncThunk(
    'project/fieldsOrFieldsschema',
    thunks.getProjectFieldsOrFieldsSchema,
);
export const getProjectDefaultAndCustomFieldsOrFieldsSchema = createAsyncThunk(
    'project/fields/allfieldsOrFieldsschema',
    thunks.getProjectDefaultAndCustomFieldsOrFieldsSchema,
);
export const saveCustomColumnsView = createAsyncThunk(
    'project/customColumnsView/save',
    thunks.saveCustomColumnsView,
);
export const deleteCustomColumnsView = createAsyncThunk(
    'project/customColumnsView/delete',
    thunks.deleteCustomColumnsView,
);

interface ProjectFieldsResponse {
    success: Record<string, unknown>;
    error: Record<string, unknown>;
}

export interface IProjectViewStructure extends SlicePropsWithData<IViewStructureNode[]> {
    /** View tree. */
    viewTree: ITreeNode<IViewStructureNode>;
}

export type RequestKeyType = 'fields' | 'schema';
export type ColumnsViewModeType = 'defaultView' | 'myView';

export interface IFieldsAndFieldsSchemaConfig {
    replicaId: string;
    viewName: string;
    requestKey: RequestKeyType;
}

export interface IDataToSave {
    fields: string;
    fieldSchema: string;
}

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectViewDataState {
    /** Data on the view structure information requested by the user. */
    structure: IProjectViewStructure;

    /** Data on the field configurations for the project view. */
    fields: SlicePropsWithData<IFieldConfig[]>;

    /** Data on the custom field configurations for the project view. */
    fieldsCustom: SlicePropsWithData<IFieldConfig[]>;

    /** View Field Schema. */
    schema: SlicePropsWithData<string[]>;

    /** View Custom Field Schema. */
    schemaCustom: SlicePropsWithData<string[]>;

    /** Mode for columns */
    columnsViewMode: ColumnsViewModeType;

    /** Check if the button is enabled. */
    dataToSave: IDataToSave | null;

    /** Defines if CustomColumnsView is saving. */
    loadingSaveCustomColumnsView: boolean;

    /** Error that is thrown during resource fetching. */
    errorSaveCustomColumnsView: SerializedError;

    /** Defines if CustomColumnsView is deleting. */
    loadingDeleteCustomColumnsView: boolean;

    /** Error that is thrown during deleting. */
    errorDeleteCustomColumnsView: SerializedError;
}

/**
 * Interface that describes data returned from the backend for a structure category in a project database view.
 */
export interface IViewStructureNode {
    /** Storage category name. */
    category: string;

    /** Number of documents in the category (including subcategories). */
    docCount: string;

    /** MD5 hash of the category name. */
    id: string;
}

/**
 * Create the redux slice.
 */
export const projectViewDataSlice = createSlice({
    name: 'projectViewData',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export function getCurrentFieldsOrFieldsSchema<TReturn = IFieldConfig>(requestKey: RequestKeyType) {
    return (store: IAppReduxStore): TReturn[] => {
        const data = store.projectViewData[requestKey].data as TReturn[];
        const dataCustom = store.projectViewData[`${requestKey}Custom`].data as TReturn[];
        if (store.projectViewData.columnsViewMode === 'defaultView') {
            return data;
        } else {
            return dataCustom.length === 0 ? data : dataCustom;
        }
    };
}

export const areFieldsEqual =
    (store: IAppReduxStore) =>
    (newCustomFields: IFieldConfig[]): boolean => {
        return store.projectViewData.columnsViewMode === 'defaultView'
            ? isEqual(
                  store.projectViewData.fields.data.map(item => ({
                      ...item,
                      visible: item.visible === undefined ? true : Boolean(item.visible),
                  })),
                  newCustomFields,
              )
            : isEqual(
                  store.projectViewData.fieldsCustom.data.map(item => ({
                      ...item,
                      visible: item.visible === undefined ? true : Boolean(item.visible),
                  })),
                  newCustomFields,
              );
    };

/**
 * Export functions from slice actions.
 */
export const { setColumnsViewMode, setDataToSave, resetCustomColumnsViewError } =
    projectViewDataSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectViewDataSlice.reducer;
